import React, { useEffect, useState } from 'react';
import { ArrowLeft, Scissors, Eraser, RefreshCw, Wand2, Layers, Sliders, ArrowRight } from 'lucide-react';
import { demofile } from '../utils/paths';
import LazyVideo from './LazyVideo';
import { useLanguage } from '../contexts/LanguageContext';
import { getT } from '../utils/translations';

interface SoundEditingIntroProps {
  onBack: () => void;
}

const COLOR_MAP: Record<string, { bg: string; text: string; border: string; badge: string }> = {
  purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700' },
  blue:   { bg: 'bg-blue-50',   text: 'text-blue-600',   border: 'border-blue-200',   badge: 'bg-blue-100 text-blue-700' },
  red:    { bg: 'bg-red-50',    text: 'text-red-600',    border: 'border-red-200',    badge: 'bg-red-100 text-red-700' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700' },
  green:  { bg: 'bg-green-50',  text: 'text-green-600',  border: 'border-green-200',  badge: 'bg-green-100 text-green-700' },
};

export default function SoundEditingIntro({ onBack }: SoundEditingIntroProps) {
  const { lang } = useLanguage();
  const t = getT(lang);
  const [activeTab, setActiveTab] = useState('voiceover');
  const [showMainContent, setShowMainContent] = useState(false);
  const [showDemoSection, setShowDemoSection] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    const t1 = window.setTimeout(() => setShowMainContent(true), 50);
    return () => clearTimeout(t1);
  }, []);
  useEffect(() => {
    const t2 = window.setTimeout(() => setShowDemoSection(true), 250);
    return () => clearTimeout(t2);
  }, []);

  const EDITING_DEMOS = lang === 'zh'
    ? [
        { id: 'voiceover',   title: '画外音消除',       icon: <Eraser size={18} />,   color: 'purple', prompt: '消除掉背景人声',
          inputs: [{ label: '输入视频', src: demofile('editing/voiceover_input.mp4') }],
          output: { label: '输出视频', src: demofile('editing/voiceover_output.mp4') } },
        { id: 'interactive', title: '交互式音效生成',   icon: <Sliders size={18} />,  color: 'blue',   prompt: '选择参考视频中的目标声源，生成对应音效',
          inputs: [
            { label: '选中右边大狗的生成结果', src: demofile('editing/interactive_input1.mp4') },
            { label: '选中左边小狗的生成结果', src: demofile('editing/interactive_input2.mp4') },
          ],
          output: { label: '两者合成后的配音效果', src: demofile('editing/interactive_output.mp4') } },
        { id: 'joint',       title: '音视频对象联合消除', icon: <Layers size={18} />,  color: 'red',    prompt: '自研视频删除模型和音效生成模型串联实现',
          inputs: [{ label: '输入视频', src: demofile('editing/joint_input.mp4') }],
          output: { label: '输出视频（吉他消除）', src: demofile('editing/joint_output.mp4') } },
        { id: 'replace',     title: '音效替换',         icon: <RefreshCw size={18} />, color: 'orange', prompt: '狗叫替换为同步的猫叫',
          inputs: [{ label: '输入视频（狗叫）', src: demofile('editing/replace_input.mp4') }],
          output: { label: '输出视频（猫叫）', src: demofile('editing/replace_output.mp4') } },
        { id: 'add',         title: '音效添加',         icon: <Scissors size={18} />, color: 'green',  prompt: '添加飞鸟叫声',
          inputs: [{ label: '输入视频（无鸟叫）', src: demofile('editing/add_input.mp4') }],
          output: { label: '输出视频（含鸟叫）', src: demofile('editing/add_output.mp4') } },
      ]
    : [
        { id: 'voiceover',   title: 'Voiceover Removal',   icon: <Eraser size={18} />,   color: 'purple', prompt: 'Remove background vocals',
          inputs: [{ label: 'Input Video', src: demofile('editing/voiceover_input.mp4') }],
          output: { label: 'Output Video', src: demofile('editing/voiceover_output.mp4') } },
        { id: 'interactive', title: 'Interactive Sound Gen', icon: <Sliders size={18} />, color: 'blue',   prompt: 'Select target sound source from reference video to generate sound',
          inputs: [
            { label: 'generation result of the right big dog', src: demofile('editing/interactive_input1.mp4') },
            { label: 'generation result of the left small dog', src: demofile('editing/interactive_input2.mp4') },
          ],
          output: { label: 'the synthesized dubbing effect of both', src: demofile('editing/interactive_output.mp4') } },
        { id: 'joint',       title: 'AV Joint Removal',    icon: <Layers size={18} />,   color: 'red',    prompt: 'Proprietary video deletion + audio generation pipeline',
          inputs: [{ label: 'Input Video', src: demofile('editing/joint_input.mp4') }],
          output: { label: 'Output Video (Guitar Removed)', src: demofile('editing/joint_output.mp4') } },
        { id: 'replace',     title: 'Sound Replacement',   icon: <RefreshCw size={18} />, color: 'orange', prompt: 'Replace dog bark with synchronized cat meow',
          inputs: [{ label: 'Input Video (Dog)', src: demofile('editing/replace_input.mp4') }],
          output: { label: 'Output Video (Cat)', src: demofile('editing/replace_output.mp4') } },
        { id: 'add',         title: 'Sound Addition',      icon: <Scissors size={18} />, color: 'green',  prompt: 'Add bird singing sound',
          inputs: [{ label: 'Input Video (No birds)', src: demofile('editing/add_input.mp4') }],
          output: { label: 'Output Video (With birds)', src: demofile('editing/add_output.mp4') } },
      ];

  const heroTags = lang === 'zh'
    ? [
        { icon: <Scissors className="text-purple-500" size={18} />, label: '精准提取' },
        { icon: <Eraser className="text-red-500" size={18} />, label: '智能消除' },
        { icon: <RefreshCw className="text-blue-500" size={18} />, label: '无痕替换' },
      ]
    : [
        { icon: <Scissors className="text-purple-500" size={18} />, label: 'Precision Extraction' },
        { icon: <Eraser className="text-red-500" size={18} />, label: 'Smart Removal' },
        { icon: <RefreshCw className="text-blue-500" size={18} />, label: 'Seamless Replacement' },
      ];

  const coreFeatures = lang === 'zh'
    ? [
        { icon: <Layers size={24} />, color: 'pink', title: '对象级分离', desc: '不同于传统频域滤波，利用深度学习实现声音对象的语义级分离，可精准提取人声、特定的乐器或环境音。' },
        { icon: <Eraser size={24} />, color: 'purple', title: '智能修复', desc: '删除特定声音后，模型会自动补全背景环境音，确保音频连贯自然，不留剪辑痕迹。' },
        { icon: <Sliders size={24} />, color: 'blue', title: '交互式编辑', desc: '支持点击视频中的物体（如"汽车"）或输入文字（如"去除风声"）来进行直观的音频编辑操作。' },
      ]
    : [
        { icon: <Layers size={24} />, color: 'pink', title: 'Object-level Separation', desc: 'Unlike traditional frequency-domain filtering, uses deep learning for semantic-level separation of sound objects.' },
        { icon: <Eraser size={24} />, color: 'purple', title: 'Intelligent Repair', desc: 'After removing specific sounds, the model automatically fills in background ambient audio, ensuring seamless audio continuity.' },
        { icon: <Sliders size={24} />, color: 'blue', title: 'Interactive Editing', desc: 'Supports clicking objects in video (e.g., "car") or typing text (e.g., "remove wind") for intuitive audio editing.' },
      ];

  const promptLabel = lang === 'zh' ? '提示词：' : 'Prompt: ';
  const effectsLabel = lang === 'zh' ? '效果展示' : 'Demo Showcase';
  const effectsSubLabel = lang === 'zh' ? '选择编辑类型，对比输入与输出效果' : 'Select an editing type to compare input and output';
  const coreFeaturesLabel = lang === 'zh' ? '核心功能' : 'Core Features';
  const heroBadge = lang === 'zh' ? '智能编辑' : 'Intelligent Editing';
  const heroTitle = lang === 'zh' ? '智能音效编辑' : 'Intelligent Sound Editing';

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 animate-in fade-in duration-300 relative">
      {/* Back button — always visible when entering from 能力介绍 */}
      <button
        onClick={onBack}
        className="fixed top-4 sm:top-6 left-4 sm:left-6 z-50 flex items-center text-gray-600 hover:text-primary transition-colors group"
      >
        <div className="p-1.5 rounded-full bg-gray-100 group-hover:bg-blue-50 transition-colors mr-2">
          <ArrowLeft className="w-4 h-4" />
        </div>
        <span className="font-medium text-sm sm:text-base">{t.common.backHome}</span>
      </button>

      {/* First frame only: title so page is not blank / no full-page spinner feel */}
      {!showMainContent && (
        <section className="pt-24 sm:pt-28 px-4 sm:px-6 max-w-[1200px] mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{heroTitle}</h1>
        </section>
      )}

      {showMainContent && (
      <>
      {/* Hero */}
      <section className="pt-20 sm:pt-32 pb-10 sm:pb-20 px-4 sm:px-6 lg:px-12 max-w-[1200px] mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-8 sm:gap-12">
          <div className="w-full lg:w-1/2">
            <div className="mb-4 sm:mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-50 text-purple-600 rounded-full text-sm font-semibold">
                <Wand2 size={14} />
                <span>{heroBadge}</span>
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 leading-tight">
              {heroTitle}
            </h1>
            <p className="text-base sm:text-xl text-gray-600 leading-relaxed mb-6 sm:mb-8">
              {lang === 'zh'
                ? '突破传统波形编辑限制，引入对象级音频编辑能力。支持通过文本或视觉提示，对视频中的特定声音进行提取、删除或替换。'
                : 'Breaks beyond traditional waveform editing, introducing object-level audio editing. Supports extracting, deleting, or replacing specific sounds via text or visual prompts.'}
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-4">
              {heroTags.map((tag, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-700 font-medium bg-gray-50 px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base">
                  {tag.icon}{tag.label}
                </div>
              ))}
            </div>
          </div>
          <div className="w-full lg:w-1/2 flex justify-center">
            <div className="relative w-full max-w-xs sm:max-w-sm lg:max-w-lg aspect-square bg-gradient-to-br from-purple-100 to-pink-50 rounded-full flex items-center justify-center p-6 sm:p-8">
              <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 rounded-full"></div>
              <div className="relative w-full h-full bg-white/50 rounded-2xl backdrop-blur-sm border border-white/60 p-4 sm:p-6 flex flex-col justify-between">
                <div className="flex items-end justify-between h-20 sm:h-24 gap-1 mb-4 sm:mb-8 px-2 sm:px-4">
                  {[...Array(20)].map((_, i) => (
                    <div
                      key={i}
                      className={`w-2 sm:w-3 rounded-full ${i > 5 && i < 12 ? 'bg-purple-500' : 'bg-gray-300'}`}
                      style={{ height: `${Math.random() * 80 + 20}%` }}
                    />
                  ))}
                </div>
                <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                      <Eraser size={18} />
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-bold">
                        {lang === 'zh' ? '消除背景噪音' : 'Remove Background Noise'}
                      </div>
                      <div className="text-xs text-gray-500">Processing...</div>
                    </div>
                  </div>
                  <div className="text-purple-600 font-bold text-sm">100%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-10 sm:py-20 bg-gray-50 px-4 sm:px-6 lg:px-12">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center">{coreFeaturesLabel}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
            {coreFeatures.map((f, i) => (
              <div key={i} className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-${f.color}-100 rounded-xl flex items-center justify-center text-${f.color}-600 mb-4 sm:mb-6`}>
                  {f.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">{f.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </>
      )}

      {/* Demo Showcase: deferred so no full-page spinners when entering from 能力介绍 */}
      {showMainContent && showDemoSection && (
      <section className="py-10 sm:py-20 px-4 sm:px-6 lg:px-12">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">{effectsLabel}</h2>
            <p className="text-gray-500 text-base sm:text-lg">{effectsSubLabel}</p>
          </div>

          {/* Tab Bar */}
          <div className="flex flex-wrap gap-2 justify-center mb-6 sm:mb-10">
            {EDITING_DEMOS.map((demo) => {
              const c = COLOR_MAP[demo.color];
              const isActive = activeTab === demo.id;
              return (
                <button
                  key={demo.id}
                  onClick={() => setActiveTab(demo.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold border transition-all duration-200 ${
                    isActive ? `${c.badge} ${c.border} shadow-sm` : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className={isActive ? c.text : 'text-gray-400'}>{demo.icon}</span>
                  {demo.title}
                </button>
              );
            })}
          </div>

          {/* Demo Card */}
          {EDITING_DEMOS.map((demo) => {
            if (demo.id !== activeTab) return null;
            const c = COLOR_MAP[demo.color];
            return (
              <div key={demo.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className={`px-5 sm:px-8 py-4 sm:py-5 border-b border-gray-100 flex items-center gap-3 ${c.bg}`}>
                  <span className={`p-2 rounded-lg bg-white shadow-sm ${c.text}`}>{demo.icon}</span>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-900">{demo.title}</h3>
                    {demo.prompt && (
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                        <span className="font-medium text-gray-700">{promptLabel}</span>{demo.prompt}
                      </p>
                    )}
                  </div>
                </div>
                <div className="p-4 sm:p-8">
                  <div className="flex flex-col lg:flex-row items-center gap-4 sm:gap-6">
                    {/* Inputs */}
                    <div className="flex flex-col gap-3 sm:gap-4 w-full lg:w-5/12">
                      {demo.inputs.map((inp, idx) => (
                        <div key={idx}>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">{inp.label}</p>
                          <div className="rounded-xl overflow-hidden bg-gray-900 aspect-video shadow">
                            <LazyVideo
                              src={inp.src}
                              className="object-contain"
                              controls
                              muted
                              playsInline
                              onPlay={(e) => { e.currentTarget.muted = false; }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Arrow */}
                    <div className="flex-shrink-0 flex items-center justify-center rotate-90 lg:rotate-0">
                      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-sm border ${c.bg} ${c.border}`}>
                        <ArrowRight size={16} className={c.text} />
                      </div>
                    </div>
                    {/* Output */}
                    <div className="w-full lg:w-5/12">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">{demo.output.label}</p>
                      <div className="rounded-xl overflow-hidden bg-gray-900 aspect-video shadow">
                        <LazyVideo
                          src={demo.output.src}
                          className="object-contain"
                          controls
                          muted
                          playsInline
                          onPlay={(e) => { e.currentTarget.muted = false; }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      )}
    </div>
  );
}
